import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, InputBar } from '~/ui';
import Edit from '~/assets/svg/newSvg/edit.svg?react';
import CheckMarkIcon from '~/assets/svg/newSvg/mark-icon.svg?react';
import Block from '~/assets/svg/newSvg/block.svg?react';
import Basket from '~/assets/svg/newSvg/basket.svg?react';
import { v4 as uuidv4 } from 'uuid';
import {
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import styles from './TournamentConditionsTable.module.scss';
import ModalConfirm from '~/components/Shared/ModalConfirm';
import { IFormikCondition } from '../../TournamentGeneral';
import { useFormik } from 'formik';
import { validate as validateTournamentCondition } from '~/utils/validateTournamentCondition';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useToastError } from '~/utils/useToastError';

interface IConditionProps {
  tournamentId: string;
  sort: {
    field: string;
    direction: string;
  };
  setSort: ({ field, direction }: { field: string; direction: string }) => void;
  isAdding: boolean;
  setIsAdding: (isAdding: boolean) => void;
  conditions: IFormikCondition[];
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  handleDeleteCondition: (id: number) => void;
  isEdit: boolean;
  status: number | undefined;
}

const TournamentConditionsTable = ({
  tournamentId,
  sort,
  setSort,
  isAdding,
  setIsAdding,
  conditions,
  setFieldValue,
  handleDeleteCondition,
  isEdit,
  status,
}: IConditionProps) => {
  const { t } = useTranslation();
  const [editingId, setEditingId] = useState('');
  const toastSuccess = useToastSuccess();
  const toastError = useToastError();
  const client = useQueryClient();

  const initialValues = {
    reward: '',
    place: '',
    id: '',
  };

  const {
    values,
    touched,
    setFieldValue: setConditionFieldValue,
    handleBlur,
    handleSubmit,
    resetForm,
    errors: conditionErrors,
  } = useFormik({
    initialValues,
    validate: validateTournamentCondition,
    onSubmit: () => {
      if (isAdding) {
        if (isEdit) {
          createCondition();
        } else {
          handleAddCondition();
        }
      } else {
        if (isEdit) {
          updateCondition(Number(editingId));
        } else {
          handleSetEditingCondition();
        }
      }
    },
  });

  const { mutateAsync: updateCondition } = useMutation(
    async (id: number) => {
      const places = values.place.split('-');

      const response = await instance.put(
        `admin/game-tournament-condition/${id}`,
        {
          from_placed: places[0],
          to_placed: places[1] ? places[1] : places[0],
          points: values.reward,
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('condition_successfully_updated'));
        client.invalidateQueries(['tournament', tournamentId]);
        setEditingId('');
        resetForm();
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { mutateAsync: createCondition } = useMutation(
    async () => {
      const places = values.place.split('-');

      const response = await instance.post(
        `admin/game-tournament-condition/${tournamentId}`,
        {
          from_placed: places[0],
          to_placed: places[1] ? places[1] : places[0],
          points: values.reward,
        }
      );

      return response.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('condition_successfully_created'));
        client.invalidateQueries(['tournament', tournamentId]);
        setIsAdding(false);
        resetForm();
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const handleAddCondition = () => {
    setIsAdding(false);
    setFieldValue('conditions', [
      ...conditions,
      {
        points: values.reward,
        place: values.place,
        id: uuidv4(),
      },
    ]);
    resetForm();
  };

  const handleEditCondition = (id: string) => {
    const editingCondition = conditions.find(
      condition => condition.id.toString() === id
    );
    if (editingCondition) {
      setConditionFieldValue('reward', editingCondition.points.toString());
      setConditionFieldValue('place', editingCondition.place);
      setConditionFieldValue('id', editingCondition.id.toString());
    }
  };

  const handleSetEditingCondition = () => {
    const newCondition = {
      points: values.reward,
      place: values.place,
      id: values.id,
    };
    const newConditionData = conditions.map(item =>
      item.id.toString() === values.id ? newCondition : item
    );
    setFieldValue('conditions', newConditionData);
    setEditingId('');
    resetForm();
  };

  const handleDelete = (id: string) => {
    if (isEdit) {
      handleDeleteCondition(Number(id));
    } else {
      const filteredConditions = conditions.filter(
        item => item.id.toString() !== id
      );
      setFieldValue('conditions', filteredConditions);
    }
  };

  return (
    <Table>
      <TableHead sort={sort} setSort={setSort}>
        <TableCellHead field="reward">{t('reward')}</TableCellHead>
        <TableCellHead field="place">{t('ranking_place')}</TableCellHead>
        {status !== 1 && status !== 3 && (
          <TableCellHead field="actions">{t('actions')}</TableCellHead>
        )}
      </TableHead>
      <TableBody>
        {isAdding && (
          <TableRow>
            <TableCell>
              <InputBar
                name="reward"
                value={values.reward}
                onChange={e => setConditionFieldValue('reward', e.target.value)}
                errors={conditionErrors.reward}
                onBlur={handleBlur}
                error={touched.reward && Boolean(conditionErrors.reward)}
              />
            </TableCell>
            <TableCell>
              <InputBar
                name="place"
                value={values.place}
                onChange={e => setConditionFieldValue('place', e.target.value)}
                errors={conditionErrors.place}
                onBlur={handleBlur}
                error={touched.place && Boolean(conditionErrors.place)}
              />
            </TableCell>
            <TableCell>
              <div className={styles.addIconsWrapper}>
                <IconButton
                  // @ts-ignore
                  onClick={e => {
                    e.stopPropagation();
                    handleSubmit();
                  }}
                >
                  <CheckMarkIcon />
                </IconButton>
                <IconButton
                  // @ts-ignore
                  onClick={e => {
                    e.stopPropagation();
                    setIsAdding(false);
                    resetForm();
                  }}
                >
                  <Block />
                </IconButton>
              </div>
            </TableCell>
          </TableRow>
        )}
        {conditions.map((item, idx) => (
          <TableRow key={idx}>
            {editingId !== item.id.toString() ? (
              <>
                <TableCell>{item.points}</TableCell>
                <TableCell>{item.place}</TableCell>
                {status !== 1 && status !== 3 && (
                  <TableCell>
                    <div className={styles.addIconsWrapper}>
                      <IconButton
                        onClick={() => {
                          setEditingId(item.id.toString());
                          handleEditCondition(item.id.toString());
                        }}
                      >
                        <Edit />
                      </IconButton>

                      <ModalConfirm
                        title={t('sure_delete_condition')}
                        description={t('delete_condition_description')}
                        onConfirm={async () => {
                          handleDelete(item.id.toString());
                        }}
                      >
                        {open => (
                          <IconButton
                            // @ts-ignore
                            onClick={event => {
                              event.stopPropagation();
                              open();
                            }}
                          >
                            <Basket />
                          </IconButton>
                        )}
                      </ModalConfirm>
                    </div>
                  </TableCell>
                )}
              </>
            ) : (
              <>
                <TableCell>
                  <InputBar
                    name="reward"
                    value={values.reward}
                    onChange={e =>
                      setConditionFieldValue('reward', e.target.value)
                    }
                    errors={conditionErrors.reward}
                    onBlur={handleBlur}
                    error={touched.reward && Boolean(conditionErrors.reward)}
                  />
                </TableCell>
                <TableCell>
                  <InputBar
                    name="place"
                    value={values.place}
                    onChange={e =>
                      setConditionFieldValue('place', e.target.value)
                    }
                    errors={conditionErrors.place}
                    onBlur={handleBlur}
                    error={touched.place && Boolean(conditionErrors.place)}
                  />
                </TableCell>
                <TableCell>
                  <div className={styles.addIconsWrapper}>
                    <IconButton
                      // @ts-ignore
                      onClick={e => {
                        e.stopPropagation();
                        handleSubmit();
                      }}
                    >
                      <CheckMarkIcon />
                    </IconButton>
                    <IconButton
                      // @ts-ignore
                      onClick={e => {
                        e.stopPropagation();
                        setEditingId('');
                        resetForm();
                      }}
                    >
                      <Block />
                    </IconButton>
                  </div>
                </TableCell>
              </>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default TournamentConditionsTable;
